import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage, languages } from '@/i18n/LanguageContext';
import { getSelfFinancialResetSettings, resetSelfFinancialData } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { SUPPORTED_CURRENCIES } from '@/lib/currency';
import { 
  AlertCircle, AlertTriangle, CheckCircle2, Loader2, Lock, Coins, Bell,
  LayoutDashboard, Receipt, Settings, ShieldCheck, LogOut, User, Menu, X,
  Wallet, PiggyBank, Tag, Repeat, BarChart3, Upload, Send, Target,
  Database, Image as ImageIcon
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const localeByLanguage = {
  es: 'es-CO',
  pt: 'pt-BR',
  en: 'en-US',
};

const SettingsPage = () => {
  const { user, logout, isAdmin, changePassword, updatePreferences, currency } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefMsg, setPrefMsg] = useState('');

  // Notification state
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifChannels, setNotifChannels] = useState([]); // ["email","telegram"]
  const [notifEmail, setNotifEmail] = useState('');
  const [notifDaysBefore, setNotifDaysBefore] = useState(3);
  const [tgToken, setTgToken] = useState('');
  const [tgChatId, setTgChatId] = useState('');
  const [tgConfigured, setTgConfigured] = useState(false); 
  const [waNumber, setWaNumber] = useState('');
  const [waConfigured, setWaConfigured] = useState(false);
  const [notifMsg, setNotifMsg] = useState('');
  const [notifMsgKind, setNotifMsgKind] = useState('info'); // info | success | error
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifSending, setNotifSending] = useState('');
  const [scheduler, setScheduler] = useState(null);
  const [resetSettings, setResetSettings] = useState({ allow_user_financial_reset: false, confirmation: '' });
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetMsgKind, setResetMsgKind] = useState('info');

  useEffect(() => {
    if (user) {
      setNotifEnabled(!!user.notifications_enabled);
      setNotifChannels(user.notification_channels || []);
      setNotifEmail(user.notification_email || '');
      setNotifDaysBefore(user.notification_days_before || 3);
      setTgChatId(user.telegram_chat_id || '');
      setTgConfigured(!!user.telegram_configured);
      setWaNumber(user.whatsapp_number || '');
      setWaConfigured(!!user.whatsapp_configured);
      setWaNumber(user.whatsapp_number || '');
      setWaConfigured(!!user.whatsapp_configured);
    }
  }, [user]);

  // Pull scheduler status (covers all users) when the page loads.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/api/notifications/status`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(setScheduler)
      .catch(() => {});
  }, []);

  useEffect(() => {
    getSelfFinancialResetSettings()
      .then(setResetSettings)
      .catch(() => setResetSettings({ allow_user_financial_reset: false, confirmation: '' }));
  }, []);

  const toggleChannel = (ch) => {
    setNotifChannels(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    );
  };

  const saveNotifications = async () => {
    setNotifSaving(true);
    setNotifMsg('');
    try {
      const payload = {
        notifications_enabled: notifEnabled,
        notification_channels: notifChannels,
        notification_email: notifEmail || null,
        notification_days_before: parseInt(notifDaysBefore, 10),
      };
      // Only send token if user typed one (avoid wiping it accidentally)
      if (tgToken.trim()) payload.telegram_bot_token = tgToken.trim();
      payload.telegram_chat_id = tgChatId.trim() || '';
      payload.whatsapp_number = waNumber.trim() || '';

      await updatePreferences(payload);
      setNotifMsg('Configuración guardada');
      setNotifMsgKind('success');
      setTgToken(''); // clear input but token stays saved server-side
      setTimeout(() => setNotifMsg(''), 3500);
    } catch (err) {
      setNotifMsg(err.response?.data?.detail || 'No se pudo guardar la configuración');
      setNotifMsgKind('error');
    } finally {
      setNotifSaving(false);
    }
  };

  const sendTest = async (channel) => {
    setNotifSending(channel);
    setNotifMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/notifications/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel, locale: localeByLanguage[language] || 'en-US' })
      });
      const data = await res.json();
      if (res.ok && data.sent) {
        setNotifMsg(`Prueba enviada por ${channel === 'email' ? 'email' : 'Telegram'} ✓`);
        setNotifMsgKind('success');
      } else {
        setNotifMsg(data.detail || data.error || `No se pudo enviar la prueba por ${channel}`);
        setNotifMsgKind('error');
      }
      setTimeout(() => setNotifMsg(''), 5000);
    } catch (err) {
      setNotifMsg(`Error enviando la prueba: ${err.message || ''}`);
      setNotifMsgKind('error');
    } finally {
      setNotifSending('');
    }
  };

  const sendNotificationsNow = async () => {
    setNotifSending('all');
    setNotifMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/notifications/send-due?force=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setNotifMsg(`Se procesaron ${data.sent || 0} pagos próximos`);
        setNotifMsgKind('success');
      } else {
        setNotifMsg(data.detail || 'No se pudo notificar ahora');
        setNotifMsgKind('error');
      }
      setTimeout(() => setNotifMsg(''), 5000);
    } catch (err) {
      setNotifMsg('Error: ' + (err.message || ''));
      setNotifMsgKind('error');
    } finally {
      setNotifSending('');
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    setPrefSaving(true);
    setPrefMsg('');
    try {
      const meta = SUPPORTED_CURRENCIES.find(c => c.code === newCurrency);
      await updatePreferences({
        currency: newCurrency,
        locale: meta?.locale
      });
      setPrefMsg('Moneda actualizada');
      setTimeout(() => setPrefMsg(''), 2500);
    } catch (err) {
      setPrefMsg(err.response?.data?.detail || 'No se pudo actualizar la moneda');
    } finally {
      setPrefSaving(false);
    }
  };

  const handleLanguageChange = async (newLanguage) => {
    changeLanguage(newLanguage);
    setPrefSaving(true);
    setPrefMsg('');
    try {
      await updatePreferences({ locale: localeByLanguage[newLanguage] || 'en-US' });
      setPrefMsg('Idioma actualizado');
      setTimeout(() => setPrefMsg(''), 2500);
    } catch (err) {
      setPrefMsg(err.response?.data?.detail || 'No se pudo actualizar el idioma');
    } finally {
      setPrefSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      setSuccess('Contraseña actualizada correctamente');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleSelfReset = async () => {
    setResetMsg('');
    setResetMsgKind('info');
    try {
      setResetLoading(true);
      const result = await resetSelfFinancialData({
        confirmation: resetConfirmation,
        restore_system_categories: true
      });
      setResetMsg(`Datos financieros reiniciados. Transacciones eliminadas: ${result.deleted?.transactions || 0}`);
      setResetMsgKind('success');
      setResetConfirmation('');
    } catch (err) {
      setResetMsg(err.response?.data?.detail || 'No fue posible reiniciar tus datos financieros');
      setResetMsgKind('error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDateTime = (value) => {
    if (!value) return '';
    const displayLocaleByLanguage = {
      es: 'es-ES',
      pt: 'pt-BR',
      en: 'en-US',
    };
    return new Date(value).toLocaleString(displayLocaleByLanguage[language] || undefined);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 bg-zinc-900 border-r border-zinc-800 p-6 z-50">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-400 hover:text-white p-2 -mr-2" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="space-y-2">
              <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <LayoutDashboard className="h-5 w-5" />
                Dashboard
              </Link>
              <Link to="/transactions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Receipt className="h-5 w-5" />
                Transacciones
              </Link>
              <Link to="/accounts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Wallet className="h-5 w-5" />
                Cuentas
              </Link>
              <Link to="/categories" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Tag className="h-5 w-5" />
                Categorías
              </Link>
              <Link to="/recurring" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Repeat className="h-5 w-5" />
                Recurrentes
              </Link>
              <Link to="/budgets" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Target className="h-5 w-5" />
                Presupuestos
              </Link>
              <Link to="/goals" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <PiggyBank className="h-5 w-5" />
                Metas y deudas
              </Link>
              <Link to="/reports" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <BarChart3 className="h-5 w-5" />
                Reportes
              </Link>
              <Link to="/receipts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <ImageIcon className="h-5 w-5" />
                Recibos
              </Link>
              <Link to="/import" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Upload className="h-5 w-5" />
                Importar
              </Link>
              <Link to="/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20" onClick={() => setMobileMenuOpen(false)}>
                <Settings className="h-5 w-5" />
                Configuración
              </Link>
              {isAdmin && (
                <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  <ShieldCheck className="h-5 w-5" />
                  Admin Panel
                </Link>
              )}
            </nav>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
                <p className="text-sm text-zinc-400">Conectado como</p>
                <p className="font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
              <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>

        <nav className="space-y-2">
          <Link 
            to="/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <LayoutDashboard className="h-5 w-5" />
            Dashboard
          </Link>
          <Link 
            to="/transactions" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <Receipt className="h-5 w-5" />
            Transacciones
          </Link>
          <Link 
            to="/accounts" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <Wallet className="h-5 w-5" />
            Cuentas
          </Link>
          <Link 
            to="/categories" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <Tag className="h-5 w-5" />
            Categorías
          </Link>
          <Link 
            to="/recurring" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <Repeat className="h-5 w-5" />
            Recurrentes
          </Link>
          <Link 
            to="/budgets" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <Target className="h-5 w-5" />
            Presupuestos
          </Link>
          <Link 
            to="/goals" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <PiggyBank className="h-5 w-5" />
            Metas y deudas
          </Link>
          <Link 
            to="/reports" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <BarChart3 className="h-5 w-5" />
            Reportes
          </Link>
          <Link 
            to="/receipts" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <ImageIcon className="h-5 w-5" />
            Recibos
          </Link>
          <Link 
            to="/import" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
          >
            <Upload className="h-5 w-5" />
            Importar
          </Link>
          <Link 
            to="/settings" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20"
          >
            <Settings className="h-5 w-5" />
            Configuración
          </Link>
          {isAdmin && (
            <Link 
              to="/admin" 
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
            >
              <ShieldCheck className="h-5 w-5" />
              Admin Panel
            </Link>
          )}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-zinc-400 hover:text-red-400"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="flex items-center gap-4 mb-8">
          <button 
            className="lg:hidden text-zinc-400 hover:text-white p-2 -ml-2 rounded-lg hover:bg-zinc-800"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Abrir menú"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-white">Configuración</h2>
            <p className="text-zinc-400">Gestiona tu cuenta y preferencias</p>
          </div>
        </div>

        <div className="grid gap-6 max-w-2xl">
          {/* Profile Card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="profile-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="h-5 w-5 text-violet-400" />
                Información de la Cuenta
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Tu información personal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-500 text-sm">Nombre</Label>
                  <p className="text-white font-medium">{user?.name}</p>
                </div>
                <div>
                  <Label className="text-zinc-500 text-sm">Email</Label>
                  <p className="text-white">{user?.email}</p>
                </div>
                <div>
                  <Label className="text-zinc-500 text-sm">Rol</Label>
                  <p className="text-violet-400 capitalize">{user?.role}</p>
                </div>
                <div>
                  <Label className="text-zinc-500 text-sm">Email Verificado</Label>
                  <p className={user?.is_verified ? 'text-emerald-400' : 'text-amber-400'}>
                    {user?.is_verified ? 'Sí' : 'No'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Language Card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="language-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Settings className="h-5 w-5 text-violet-400" />
                Idioma del sitio
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Elige el idioma que quieres usar dentro del sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="settings-language-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-2">
                Idioma actual: {languages.find((lang) => lang.code === language)?.name || language}
              </p>
            </CardContent>
          </Card>

          {/* Currency Preferences Card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="currency-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Coins className="h-5 w-5 text-violet-400" />
                Moneda
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Elige la moneda en que se mostrarán todos tus montos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="currency-select" className="text-zinc-300">Moneda preferida</Label>
                <Select
                  value={currency}
                  onValueChange={handleCurrencyChange}
                  disabled={prefSaving}
                >
                  <SelectTrigger
                    id="currency-select"
                    className="bg-zinc-950 border-zinc-800 mt-2"
                    data-testid="currency-select-trigger"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_CURRENCIES.map(c => (
                      <SelectItem key={c.code} value={c.code} data-testid={`currency-opt-${c.code}`}>
                        <span className="inline-block w-12 font-mono">{c.symbol}</span>
                        <span className="ml-2">{c.label} ({c.code})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {prefMsg && (
                <p className="text-sm text-emerald-400 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" /> {prefMsg}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Notifications Card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="notifications-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Bell className="h-5 w-5 text-violet-400" />
                Notificaciones
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Recibe avisos por email, Telegram o WhatsApp cuando se acerque la fecha de pago
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Master switch */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-zinc-300">Notificaciones activadas</Label>
                  <p className="text-xs text-zinc-500 mt-1">
                    Si las desactivas, no se enviarán avisos automáticos
                  </p>
                </div>
                <Switch
                  checked={notifEnabled}
                  onCheckedChange={setNotifEnabled}
                  data-testid="notif-toggle"
                  aria-label="Activar notificaciones"
                />
              </div>

              {/* Channels */}
              <div>
                <Label className="text-zinc-300 mb-2 block">Canales</Label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => toggleChannel('email')}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      notifChannels.includes('email')
                        ? 'bg-violet-500/20 border-violet-500/40 text-violet-200'
                        : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'
                    }`}
                    data-testid="channel-email"
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleChannel('telegram')}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      notifChannels.includes('telegram')
                        ? 'bg-violet-500/20 border-violet-500/40 text-violet-200'
                        : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'
                    }`}
                    data-testid="channel-telegram"
                  >
                    Telegram
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleChannel('whatsapp')}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      notifChannels.includes('whatsapp')
                        ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200'
                        : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'
                    }`}
                    data-testid="channel-whatsapp"
                  >
                    WhatsApp
                  </button>
                </div>
              </div>

              {/* Days before */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="notif-days" className="text-zinc-300">Avisar X días antes</Label>
                  <Select value={String(notifDaysBefore)} onValueChange={(v) => setNotifDaysBefore(parseInt(v, 10))}>
                    <SelectTrigger id="notif-days" className="bg-zinc-950 border-zinc-800 mt-2" data-testid="notif-days-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 5, 7, 10, 14].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} día{n === 1 ? '' : 's'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="notif-email" className="text-zinc-300">Email destino</Label>
                  <Input
                    id="notif-email"
                    type="email"
                    value={notifEmail}
                    onChange={(e) => setNotifEmail(e.target.value)}
                    placeholder={user?.email || ''}
                    className="bg-zinc-950 border-zinc-800 mt-2"
                    data-testid="notif-email-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Si lo dejas vacío, se usa <code className="text-zinc-400">{user?.email}</code>
                  </p>
                </div>
              </div>

              {/* Telegram */}
              <details className="rounded-lg border border-zinc-800 bg-zinc-950/50 group" data-testid="telegram-section">
                <summary className="cursor-pointer px-4 py-3 text-sm text-zinc-300 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4 text-violet-400" />
                    Configurar Telegram {tgConfigured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">activo</span>}
                  </span>
                  <span className="text-zinc-600 text-xs group-open:hidden">expandir</span>
                </summary>
                <div className="p-4 pt-2 space-y-3">
                  <div>
                    <Label htmlFor="tg-token" className="text-zinc-300 text-xs">Bot Token</Label>
                    <Input
                      id="tg-token"
                      type="password"
                      value={tgToken}
                      onChange={(e) => setTgToken(e.target.value)}
                      placeholder={tgConfigured ? '••••••••• (ya configurado, deja vacío para mantener)' : '123456:ABCDEF…'}
                      className="bg-zinc-900 border-zinc-700 mt-2"
                      data-testid="tg-token-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tg-chat" className="text-zinc-300 text-xs">Chat ID</Label>
                    <Input
                      id="tg-chat"
                      value={tgChatId}
                      onChange={(e) => setTgChatId(e.target.value)}
                      placeholder="123456789"
                      className="bg-zinc-900 border-zinc-700 mt-2"
                      data-testid="tg-chat-input"
                    />
                  </div>
                  <details className="rounded bg-zinc-900/50 p-3 text-xs text-zinc-400">
                    <summary className="cursor-pointer text-violet-300">¿Cómo obtengo bot token y chat ID?</summary>
                    <ol className="list-decimal list-inside space-y-1 mt-2 ml-1">
                      <li>Abre Telegram y busca <code className="text-zinc-300">@BotFather</code>.</li>
                      <li>Escribe <code className="text-zinc-300">/newbot</code> y sigue las instrucciones.</li>
                      <li>BotFather te da un <strong>token</strong> (formato <code>123:ABC…</code>). Pégalo arriba.</li>
                      <li>Abre tu bot recién creado y escríbele <code className="text-zinc-300">/start</code>.</li>
                      <li>Visita <code className="break-all text-zinc-300">https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> y copia el <code>chat.id</code> que aparece.</li>
                    </ol>
                  </details>
                </div>
              </details>

              {/* WhatsApp */}
              <details className="rounded-lg border border-zinc-800 bg-zinc-950/50 group" data-testid="whatsapp-section">
                <summary className="cursor-pointer px-4 py-3 text-sm text-zinc-300 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4 text-emerald-400" />
                    Configurar WhatsApp {waConfigured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">activo</span>}
                  </span>
                  <span className="text-zinc-600 text-xs group-open:hidden">expandir</span>
                </summary>
                <div className="p-4 pt-2 space-y-3">
                  <div>
                    <Label htmlFor="wa-number" className="text-zinc-300 text-xs">Número de WhatsApp</Label>
                    <Input
                      id="wa-number"
                      value={waNumber}
                      onChange={(e) => setWaNumber(e.target.value)}
                      placeholder="5511999999999 (con código de país, sin +)"
                      className="bg-zinc-900 border-zinc-700 mt-2"
                      data-testid="wa-number-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Solo dígitos. Brasil: <code className="text-zinc-400">55</code> + DDD + número (ej. <code className="text-zinc-400">5511987654321</code>).
                    </p>
                  </div>
                  <div className="rounded bg-zinc-900/50 p-3 text-xs text-zinc-400">
                    <p>
                      WhatsApp usa una conexión central configurada por el administrador del servidor.
                      Si el botón "Probar WhatsApp" devuelve un error de "no configurado", avísale al
                      admin que cargue <code className="text-zinc-300">EVOLUTION_API_URL</code>,{' '}
                      <code className="text-zinc-300">EVOLUTION_API_KEY</code> y{' '}
                      <code className="text-zinc-300">EVOLUTION_INSTANCE</code> en el <code>.env</code>.
                    </p>
                  </div>
                </div>
              </details>

              {/* Scheduler info */}
              {scheduler && (
                <div className="rounded-lg bg-zinc-950/50 border border-zinc-800 p-3 text-xs text-zinc-400" data-testid="scheduler-info">
                  <p className="flex items-center gap-2 text-zinc-300 mb-1">
                    <Bell className="h-3.5 w-3.5 text-violet-400" />
                    Envío automático diario a las <span className="text-white font-medium">{String(scheduler.send_hour_utc).padStart(2,'0')}:00 UTC</span>
                  </p>
                  {scheduler.next_run_at && (
                    <p>Próxima ejecución: <span className="text-zinc-200">{formatDateTime(scheduler.next_run_at)}</span></p>
                  )}
                  {scheduler.last_run_at && (
                    <p>Última ejecución: <span className="text-zinc-200">{formatDateTime(scheduler.last_run_at)}</span> · {scheduler.last_run_users} usuario(s), {scheduler.last_run_sent} aviso(s)</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
                <Button
                  onClick={saveNotifications}
                  className="bg-violet-600 hover:bg-violet-700"
                  disabled={notifSaving}
                  data-testid="save-notif-btn"
                >
                  {notifSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Guardar configuración
                </Button>
                <Button
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800 text-zinc-200"
                  onClick={() => sendTest('email')}
                  disabled={notifSending !== ''}
                  data-testid="test-email-btn"
                >
                  {notifSending === 'email' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />}
                  Probar email
                </Button>
                <Button
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800 text-zinc-200"
                  onClick={() => sendTest('telegram')}
                  disabled={notifSending !== '' || !tgConfigured}
                  data-testid="test-telegram-btn"
                  title={!tgConfigured ? 'Configura el bot primero' : 'Enviar prueba'}
                >
                  {notifSending === 'telegram' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Probar Telegram
                </Button>
                <Button
                  variant="outline"
                  className="border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300"
                  onClick={() => sendTest('whatsapp')}
                  disabled={notifSending !== '' || !waNumber.trim()}
                  data-testid="test-whatsapp-btn"
                  title={!waNumber.trim() ? 'Carga tu número primero' : 'Enviar prueba por WhatsApp'}
                >
                  {notifSending === 'whatsapp' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Probar WhatsApp
                </Button>
                <Button
                  variant="outline"
                  className="border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300"
                  onClick={sendNotificationsNow}
                  disabled={notifSending !== '' || !notifEnabled}
                  data-testid="send-now-btn"
                  title={!notifEnabled ? 'Activa notificaciones primero' : ''}
                >
                  Notificar ahora
                </Button>
              </div>

              {notifMsg && (
                <p className={`text-sm flex items-center gap-2 ${
                  notifMsgKind === 'success' ? 'text-emerald-400' :
                  notifMsgKind === 'error' ? 'text-red-400' : 'text-zinc-400'
                }`} data-testid="notif-msg">
                  {notifMsgKind === 'success'
                    ? <CheckCircle2 className="h-4 w-4" />
                    : notifMsgKind === 'error'
                    ? <AlertCircle className="h-4 w-4" />
                    : null}
                  {notifMsg}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card className="bg-zinc-900/50 border-red-500/30" data-testid="danger-zone-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                Zona de peligro
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Reinicia tus datos financieros sin borrar tu usuario ni tus preferencias de notificación
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                <p className="text-sm text-red-200 font-medium">Reiniciar mis datos financieros</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Borra cuentas, transacciones, presupuestos, metas, deudas, recurrentes, recibos, categorías financieras y facturas importadas. Conserva email, contraseña, idioma, moneda, Telegram y WhatsApp.
                </p>
              </div>

              {!resetSettings.allow_user_financial_reset ? (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
                  El administrador deshabilitó el reset financiero para usuarios.
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="self-reset-confirmation" className="text-zinc-300">
                      Escribe {resetSettings.confirmation || `RESET ${user?.email}`}
                    </Label>
                    <Input
                      id="self-reset-confirmation"
                      value={resetConfirmation}
                      onChange={(e) => setResetConfirmation(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 mt-2"
                      placeholder={resetSettings.confirmation || `RESET ${user?.email}`}
                      data-testid="self-reset-confirmation-input"
                    />
                  </div>
                  <Button
                    className="bg-red-600 hover:bg-red-700"
                    onClick={handleSelfReset}
                    disabled={resetLoading || resetConfirmation !== (resetSettings.confirmation || `RESET ${user?.email}`)}
                    data-testid="self-reset-financial-btn"
                  >
                    {resetLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                    Reiniciar mis datos financieros
                  </Button>
                </>
              )}

              {resetMsg && (
                <p className={`text-sm flex items-center gap-2 ${
                  resetMsgKind === 'success' ? 'text-emerald-400' :
                  resetMsgKind === 'error' ? 'text-red-400' : 'text-zinc-400'
                }`} data-testid="self-reset-msg">
                  {resetMsgKind === 'success'
                    ? <CheckCircle2 className="h-4 w-4" />
                    : resetMsgKind === 'error'
                    ? <AlertCircle className="h-4 w-4" />
                    : null}
                  {resetMsg}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="password-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lock className="h-5 w-5 text-violet-400" />
                Cambiar Contraseña
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Actualiza tu contraseña de acceso
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </div>
                )}

                {success && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
                    <CheckCircle2 className="h-4 w-4" />
                    {success}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-zinc-300">Contraseña Actual</Label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 focus:border-violet-500"
                    data-testid="current-password-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Nueva Contraseña</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 focus:border-violet-500"
                    data-testid="new-password-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Confirmar Nueva Contraseña</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 focus:border-violet-500"
                    data-testid="confirm-new-password-input"
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="bg-violet-600 hover:bg-violet-700"
                  disabled={loading}
                  data-testid="change-password-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Actualizando...
                    </>
                  ) : (
                    'Cambiar Contraseña'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
